import React, { useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import classnames from 'classnames';
import useEChart from '@/pages/intelligence/pages/Compare/PcConsoleGames/hooks/useEChart';
import { getChartStaticOptionData, getChartInitialOptionData, getChartDynamicOptionData } from './utils';
import { ECHART_NODE_HEIGHT } from '@/pages/intelligence/pages/Compare/PcConsoleGames/constants/echart';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IEChartDataEntity,
  ITooltipComponentOption,
  ILegendComponentOption,
  IEChartInstance,
} from '@/pages/intelligence/types/echart';
import './index.scss';

export interface IDBIBaseLineChartInstance {
  getChartNode: () => HTMLElement | null;
  getChartInstance: () => IEChartInstance | null;
}

export interface IDBIBaseLineChartProps extends IBaseProps {
  locale: any;
  chartNodeHeight?: number;
  xAxisEncode?: string;
  xAxisDataSortter?: (xAxisData: string[]) => string[];
  xAxisData?: string[];
  dimensions?: string[];
  sources: IEChartDataEntity[];
  formatters?: {
    serieNameFormatter?: (name: string) => string;
    xAxisLabelFormatter?: (name: string) => string;
    yAxisLabelFormatter?: (name: string) => string;
  };
  color?: string[];
  tooltipTitle?: string;
  tooltipOption?: ITooltipComponentOption;
  legendOption?: ILegendComponentOption | ILegendComponentOption[];
}

function DBIBaseLineChart(props: IDBIBaseLineChartProps, ref: React.Ref<IDBIBaseLineChartInstance>) {
  const {
    locale,
    chartNodeHeight = ECHART_NODE_HEIGHT,
    xAxisEncode,
    xAxisDataSortter,
    xAxisData,
    dimensions,
    sources,
    formatters,
    color,
    tooltipTitle,
    tooltipOption,
    legendOption,
    style,
    className,
  } = props;

  /**
   * 获取echarts的初始化配置（静态配置，不依赖数据动态更新的配置）、和初始化图例选中状态集
   */
  const initialOption = useMemo(() => {
    const { serieNameFormatter, xAxisLabelFormatter, yAxisLabelFormatter } = formatters ?? {};
    return getChartInitialOptionData({
      tooltipTitle,
      tooltipOption,
      legendOption,
      serieNameFormatter,
      xAxisLabelFormatter,
      yAxisLabelFormatter,
    });
  }, [formatters]);

  const { chartNode, option, setOption, getChartInstance } = useEChart({
    initialOption,
  });

  // 数据变化时,更新echart配置项
  useEffect(() => {
    const { serieNameFormatter } = formatters ?? {};
    const staticOption = getChartStaticOptionData();
    const legendOptionTemp = Array.isArray(legendOption)
      ? legendOption.map((item) => ({ ...staticOption?.legend, ...item, formatter: serieNameFormatter }))
      : { ...staticOption?.legend, ...legendOption, formatter: serieNameFormatter };
    const dynamicOptionTemp = getChartDynamicOptionData(sources, {
      xAxisEncode,
      xAxisDataSortter,
      xAxisData,
      dimensions,
    });

    const optionTemp = {
      ...option,
      color,
      legend: legendOptionTemp,
      xAxis: {
        ...option?.xAxis,
        data: dynamicOptionTemp?.xAxis?.data || [],
      },
      series: dynamicOptionTemp?.series || [],
      dataset: {
        ...option?.dataset,
        dimensions: dynamicOptionTemp?.dataset?.dimensions || [],
        source: dynamicOptionTemp?.dataset?.source || [],
      },
    };
    setOption?.(optionTemp);
  }, [locale, xAxisEncode, xAxisDataSortter, xAxisData, dimensions, sources, color, legendOption, formatters]);

  useImperativeHandle(ref, () => ({
    getChartNode: () => chartNode.current,
    getChartInstance: () => getChartInstance(),
  }));

  return (
    <div style={style} className={classnames('dbi-common-base-line-chart', className)}>
      <div ref={chartNode} style={{ height: chartNodeHeight }} className="dbi-common-base-line-chart_chart-node"></div>
    </div>
  );
}

export default forwardRef(DBIBaseLineChart);
